/**
 * The log in its own window, for a second monitor. The game window renders into it and handles
 * its chat box; log.html only asks to be attached (again, after the game window reloads) and
 * closes itself when the game window is gone. While it's open, the log in the game window
 * collapses to a bar so the map gets the room.
 */
import type { Codex, LogLineView, PlayerView } from '../shared/view.ts';
import { renderChat } from './chat.ts';
import { installRulesTooltip } from './rules.ts';
import { currentTalk } from './talk.ts';

const MAX_LINES = 1500;
const WINDOW_NAME = 'crawler-log';
/** Tells this game window's attachments apart from a previous page load's. */
const PAGE_ID = Math.random().toString(36).slice(2);

/** Every log line seen since the page loaded (the server sends only the latest 120). */
export class LogHistory {
  lines: LogLineView[] = [];

  merge(log: readonly LogLineView[]): void {
    const tail = this.lines.at(-1)?.seq ?? 0;
    // A newer log that starts over means the server restarted.
    if ((log.at(-1)?.seq ?? 0) < tail) this.lines = [];
    const from = this.lines.at(-1)?.seq ?? 0;
    for (const line of log) if (line.seq > from) this.lines.push(line);
    if (this.lines.length > MAX_LINES) this.lines.splice(0, this.lines.length - MAX_LINES);
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function savedPlacement(): string {
  try {
    const p = JSON.parse(localStorage.getItem('crawler.logWindow') ?? 'null') as { x: number; y: number; w: number; h: number } | null;
    if (p && p.w > 200 && p.h > 200) return `,width=${Math.round(p.w)},height=${Math.round(p.h)},left=${Math.round(p.x)},top=${Math.round(p.y)}`;
  } catch {
    // Use the default size.
  }
  return ',width=520,height=820';
}

export interface LogPopoutOptions {
  room: string;
  view: () => PlayerView | null;
  codex: () => Codex | null;
  say: (text: string) => void;
  /** The pop-out opened or closed (the game window shows or hides its own log). */
  onChange: (open: boolean) => void;
}

export class LogPopout {
  private win: Window | null = null;
  private readonly history = new LogHistory();
  private readonly opts: LogPopoutOptions;

  constructor(opts: LogPopoutOptions) {
    this.opts = opts;
    (window as unknown as { crawlerAttachLog: (w: Window) => void }).crawlerAttachLog = (w) => this.attach(w);
    window.setInterval(() => {
      if (this.win?.closed) this.detach();
    }, 1000);
  }

  get open(): boolean {
    return !!this.win && !this.win.closed;
  }

  /**
   * Opens the window (or brings it forward). 'blocked': the browser stopped the pop-up;
   * 'unsupported': it would have opened in the game's own tab (some embedded browsers do).
   */
  pop(): 'ok' | 'blocked' | 'unsupported' {
    if (this.open) {
      this.win!.focus();
      return 'ok';
    }
    // An empty URL never navigates: it opens a blank window, or finds the one already open.
    const w = window.open('', WINDOW_NAME, `popup${savedPlacement()}`);
    if (!w) return 'blocked';
    if (w === window || w.document === document) return 'unsupported';
    if (!w.location.pathname.endsWith('log.html')) w.location.href = new URL('log.html', location.href).href;
    w.focus();
    // log.html attaches itself once it has loaded.
    return 'ok';
  }

  /** Puts the log back in the game window. */
  dock(): void {
    const w = this.win;
    this.detach();
    w?.close();
  }

  /** Feed every view: the history grows, and the pop-out (if open) is redrawn. */
  update(view: PlayerView): void {
    this.history.merge(view.log);
    if (this.open) this.render(view);
  }

  private attach(w: Window): void {
    const doc = w.document;
    if (this.win === w && doc.documentElement.dataset.attached === PAGE_ID) return;
    this.win = w;
    doc.documentElement.dataset.attached = PAGE_ID;
    doc.title = `Log · Torchlight Crawler (${this.opts.room})`;
    // A fresh body drops anything a previous load of the game window attached.
    doc.getElementById('rules-tip')?.remove();
    doc.body.innerHTML = `
      <header id="popout-head"><b>Log</b><span class="muted">room “${esc(this.opts.room)}”</span><span class="spacer"></span>
        <button type="button" class="small ghost" data-dock title="Put the log back in the game window">Dock</button></header>
      <div id="log"></div>
      <form id="chat-form" autocomplete="off">
        <span id="talk-target"></span>
        <input id="chat-input" maxlength="300" aria-label="Chat">
        <button type="submit" class="small">Send</button>
      </form>`;
    installRulesTooltip(this.opts.codex, doc);
    const input = doc.getElementById('chat-input') as HTMLInputElement;
    doc.getElementById('chat-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      this.opts.say(text);
      input.value = '';
    });
    doc.querySelector('[data-dock]')!.addEventListener('click', () => this.dock());
    const view = this.opts.view();
    if (view) {
      this.history.merge(view.log);
      this.render(view);
    }
    this.opts.onChange(true);
  }

  private detach(): void {
    if (!this.win) return;
    this.win = null;
    this.opts.onChange(false);
  }

  private render(view: PlayerView): void {
    const doc = this.win!.document;
    const log = doc.getElementById('log');
    if (!log) return;
    renderChat(log, this.history.lines);
    // The chat box says who will hear you, like the one in the game window.
    const talk = doc.getElementById('talk-target');
    const source = document.getElementById('talk-target');
    if (talk && source && talk.innerHTML !== source.innerHTML) talk.innerHTML = source.innerHTML;
    const input = doc.getElementById('chat-input') as HTMLInputElement | null;
    const recall = view.recall ? currentTalk(view) : undefined;
    const placeholder = recall?.recallId ? recall.placeholder
      : view.talk ? `Say something to ${view.talk.name}… (Enter to send)` : 'Message the party… (Enter to send)';
    if (input && input.placeholder !== placeholder) input.placeholder = placeholder;
  }
}
